import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronUp,
  CircleCheck,
  Info,
  Maximize,
  Minimize,
  Monitor,
  RotateCw,
  Smartphone,
  type LucideIcon,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CreativePreviewEventPanel } from '@/components/CreativePreviewEventPanel'
import { CreativeLivePreviewForm } from '@/components/CreativeLivePreviewForm'
import { AuthService } from '@/services/core/auth'
import { CreativesApi } from '@/services/creatives/creativesApi'
import { CLICK_CHECK_PARAM, PREVIEW_WINDOW_NAME } from '@/services/creatives/previewApi'
import {
  announceClickCheck,
  onClickCheck,
  type ClickCheckResult,
} from '@/services/creatives/clickCheckChannel'
import { LayoutsApi } from '@/services/sites/layoutsApi'
import {
  MOBILE_VIEWPORT,
  isPreviewEventMessage,
  type PreviewDevice,
  type PreviewEvent,
} from '@/services/creatives/previewEvents'

/** Where the creative is previewed: on a blank page here, or on a real site through a live link. */
type PreviewWebsite = 'blank' | 'live'

function ToolbarButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="icon" onClick={onClick} aria-label={label}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

const DEVICES: Array<{ value: PreviewDevice; icon: LucideIcon; labelKey: string }> = [
  { value: 'desktop', icon: Monitor, labelKey: 'creatives.previewDesktop' },
  { value: 'mobile', icon: Smartphone, labelKey: 'creatives.previewMobile' },
]

/**
 * A saved creative in a preview window of its own: the creative on a blank
 * page as a desktop or a phone shows it, above the events it fires. The
 * creative renders in a frame sized to the device, so layouts that size
 * themselves to the viewport behave as they would on that device. Signed-in
 * users can also build a live link that shows it on a real site.
 */
export function CreativeBlankPreviewPage() {
  const { creativeId } = useParams<{ creativeId: string }>()
  const [params, setParams] = useSearchParams()
  const { t } = useTranslation()
  const frame = useRef<HTMLIFrameElement>(null)

  const [device, setDevice] = useState<PreviewDevice>(
    params.get('device') === 'mobile' ? 'mobile' : 'desktop'
  )
  const [events, setEvents] = useState<PreviewEvent[]>([])
  const [loadCount, setLoadCount] = useState(0)
  const [panelOpen, setPanelOpen] = useState(false)
  const [website, setWebsite] = useState<PreviewWebsite>('blank')
  const [fullScreen, setFullScreen] = useState(false)
  /**
   * Routes the ad's click back here instead of to its destination, so the
   * click is proof that the ad server could count it. Landing on the external
   * page instead means it could not.
   */
  const [checkClick, setCheckClick] = useState(false)

  /**
   * The flag has to be on *this* window's URL, not only on the frame's: the
   * ad server reads the referrer the ad request recorded, and adn.js reports
   * the window the preview is shown in. The HTTP Referer of the click is only
   * a fallback the server never reaches when the token carries one.
   */
  useEffect(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (!checkClick) {
          next.delete(CLICK_CHECK_PARAM)
          return next
        }
        // Where the click comes back to: this page, without the arming
        // parameters, so landing here does not re-arm the check.
        const back = new URL(window.location.href)
        back.searchParams.delete(CLICK_CHECK_PARAM)
        back.searchParams.delete('redirectUrl')
        back.searchParams.set('checkingTheClick', 'true')
        next.set(CLICK_CHECK_PARAM, back.toString())
        return next
      },
      { replace: true }
    )
  }, [checkClick, setParams])

  // How a click that came back reports itself: the ad server adds these on the
  // way, and they are stripped once read so a reload is a clean preview again.
  const [clickResult, setClickResult] = useState<ClickCheckResult | null>(null)
  useEffect(() => {
    if (params.get('checkingTheClick') !== 'true') return
    const result = { redirectUrl: params.get('redirectUrl') ?? '' }
    setClickResult(result)
    // The click rarely lands in the window showing the preview, so the result
    // is told to every window of the app, not only to this one.
    announceClickCheck(result)
    // A tab the click opened has nothing else to show, so it closes itself.
    // The preview window has an opener too, so it is told apart by its name,
    // and a window the user was already in keeps its page either way.
    if (window.opener && window.name !== PREVIEW_WINDOW_NAME) {
      window.close()
      return
    }
    // Cleared through the router, so the frame is rebuilt from the same
    // parameters the window is showing.
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('checkingTheClick')
        next.delete('redirectUrl')
        return next
      },
      { replace: true }
    )
  }, [params, setParams])

  // A click that came back in another window reports itself here.
  useEffect(() => onClickCheck(setClickResult), [])

  // The live link needs the creative itself, which only a signed-in user can load
  const signedIn = AuthService.isAuthenticated()
  const { data: creative } = useQuery({
    queryKey: ['creative', creativeId],
    queryFn: () => CreativesApi.getById(creativeId!),
    enabled: signedIn && !!creativeId,
    staleTime: 5 * 60 * 1000,
  })

  // A third-party creative handles its own clicks, so the check does not apply
  // to it. Only the layout knows that, not the creative.
  const { data: layout } = useQuery({
    queryKey: ['layout', creative?.layoutId],
    queryFn: () => LayoutsApi.getById(creative!.layoutId),
    enabled: signedIn && !!creative?.layoutId,
    staleTime: 5 * 60 * 1000,
  })
  const canCheckClick = !!layout && layout.layoutType !== 'THIRD_PARTY'

  const name = params.get('name') ?? creative?.name
  useEffect(() => {
    document.title = name
      ? t('creatives.previewWindowTitleNamed', { name })
      : t('creatives.previewBlankPageTitle')
  }, [name, t])

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return
      if (isPreviewEventMessage(e.data)) setEvents((current) => [...current, e.data.event])
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    const onChange = () => setFullScreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  // Each load starts the creative afresh, so its events start afresh too
  const reload = () => {
    setEvents([])
    setLoadCount((count) => count + 1)
  }

  const changeCheckClick = (value: boolean) => {
    setCheckClick(value)
    setClickResult(null)
    reload()
  }

  const changeDevice = (value: PreviewDevice) => {
    setDevice(value)
    reload()
  }

  // The blank page renders the creative again on the way back, so it gets fresh events
  const changeWebsite = (value: PreviewWebsite) => {
    setWebsite(value)
    if (value === 'blank') reload()
  }

  const toggleFullScreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void document.documentElement.requestFullscreen()
  }

  const frameParams = new URLSearchParams(params)
  frameParams.delete('name')
  frameParams.delete('device')
  frameParams.delete('checkingTheClick')
  frameParams.delete('redirectUrl')
  const frameSrc = `/preview-creative/${encodeURIComponent(creativeId ?? '')}/frame?${frameParams}`

  const showLive = website === 'live' && !!creative

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="flex items-end gap-6 border-b bg-card px-4 py-2">
        {creative && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="preview-website" className="text-xs text-(--ad-text-muted)">
              {t('creatives.previewWebsite')}
            </Label>
            <Select value={website} onValueChange={(v) => changeWebsite(v as PreviewWebsite)}>
              <SelectTrigger id="preview-website" className="w-40">
                <SelectValue>
                  {website === 'live'
                    ? t('creatives.livePreview')
                    : t('creatives.previewBlankPage')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">{t('creatives.previewBlankPage')}</SelectItem>
                <SelectItem value="live">{t('creatives.livePreview')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {!showLive && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-(--ad-text-muted)">{t('creatives.previewMode')}</span>
            <Tabs value={device} onValueChange={(v) => changeDevice(v as PreviewDevice)}>
              <TabsList aria-label={t('creatives.previewMode')} className="h-9">
                {DEVICES.map(({ value, icon: Icon, labelKey }) => (
                  <Tooltip key={value}>
                    {/* A trigger of its own, since the tooltip's data-state would hide the active tab */}
                    <TooltipTrigger asChild>
                      <span className="inline-flex h-full">
                        <TabsTrigger value={value} aria-label={t(labelKey)}>
                          <Icon />
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{t(labelKey)}</TooltipContent>
                  </Tooltip>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}
        {!showLive && canCheckClick && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="preview-check-click" className="text-xs text-(--ad-text-muted)">
              {t('creatives.previewCheckClick')}
            </Label>
            {/* Boxed to the height of the controls beside it, so the labels line up. */}
            <div className="flex h-9 items-center">
              <Checkbox
                id="preview-check-click"
                className="size-5"
                checked={checkClick}
                onCheckedChange={(value) => changeCheckClick(!!value)}
              />
            </div>
          </div>
        )}
        {/* Separates the preview's controls from the creative being previewed. */}
        {name && <Separator orientation="vertical" className="mb-1.5 !h-9" />}
        {name && (
          <p className="min-w-0 flex-1 truncate pb-2 text-sm text-(--ad-text-muted)">{name}</p>
        )}
        <div className="ml-auto flex items-center gap-1 pb-0.5">
          {!showLive && (
            <ToolbarButton label={t('creatives.previewReload')} onClick={reload}>
              <RotateCw />
            </ToolbarButton>
          )}
          <ToolbarButton
            label={
              fullScreen ? t('creatives.previewExitFullScreen') : t('creatives.previewFullScreen')
            }
            onClick={toggleFullScreen}
          >
            {fullScreen ? <Minimize /> : <Maximize />}
          </ToolbarButton>
        </div>
      </header>

      {/* Padded by the wrapper: the alert is `w-full`, so a horizontal margin
          would push it past the window instead of inset it. */}
      {(clickResult || checkClick) && (
        <div className="shrink-0 px-4 pt-3">
        {clickResult && (
          <Alert variant="success">
            <CircleCheck className="size-4" />
            <AlertDescription className="flex flex-col gap-1">
              <span>{t('creatives.previewClickCounted')}</span>
              {clickResult.redirectUrl && (
                <a
                  href={clickResult.redirectUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all underline"
                >
                  {clickResult.redirectUrl}
                </a>
              )}
            </AlertDescription>
          </Alert>
        )}

        {checkClick && !clickResult && (
          <Alert variant="info">
            <Info className="size-4" />
            <AlertDescription>{t('creatives.previewCheckClickHint')}</AlertDescription>
          </Alert>
        )}
        </div>
      )}

      <main
        className={cn(
          'flex min-h-0 flex-1 overflow-auto p-4',
          (device === 'mobile' || showLive) && 'items-start justify-center'
        )}
      >
        {showLive ? (
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>{t('creatives.livePreview')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CreativeLivePreviewForm creative={creative} enabled />
            </CardContent>
          </Card>
        ) : (
          <iframe
            key={`${device}-${loadCount}`}
            ref={frame}
            src={frameSrc}
            title={t('creatives.previewBlankPageTitle')}
            className={cn(
              'shrink-0 bg-card',
              device === 'desktop'
                ? 'size-full rounded-xl border shadow-sm'
                : 'rounded-4xl border-8 border-(--ad-text) shadow-lg'
            )}
            style={
              device === 'mobile'
                ? // The border sits outside the emulated viewport, as a phone's bezel does
                  {
                    width: MOBILE_VIEWPORT.width + 16,
                    height: MOBILE_VIEWPORT.height + 16,
                    boxSizing: 'border-box',
                  }
                : undefined
            }
          />
        )}
      </main>

      {!showLive && (
        // Tucked away at the bottom until asked for, so the creative gets the room
        <Collapsible
          open={panelOpen}
          onOpenChange={setPanelOpen}
          className="shrink-0 border-t bg-card"
        >
          <CollapsibleTrigger
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium hover:bg-(--ad-neutral-soft)"
            aria-label={
              panelOpen ? t('creatives.previewHideEvents') : t('creatives.previewShowEvents')
            }
          >
            {t('creatives.previewEvents')}
            {events.length > 0 && <Badge variant="secondary">{events.length}</Badge>}
            <ChevronUp
              className={cn('ml-auto size-4 transition-transform', panelOpen && 'rotate-180')}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="h-80 bg-background p-4">
            <CreativePreviewEventPanel events={events} />
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}
